// Kadrlar (HRM) — `xsib-hr` servisi bilan kontrakt.
//
//   GET    /api/hr/persons?q&state&orgNodeId&page&size  → {rows, total, page, size}
//   GET    /api/hr/persons/{id}                         → PersonView
//   POST   /api/hr/persons  {person, assignment}        → PersonView (201)
//   PUT    /api/hr/persons/{id}  PersonDraft            → PersonView
//   POST   /api/hr/persons/{id}/termination             → OperationResult
//   POST   /api/hr/persons/{id}/state  {state}          → OperationResult
//
//   GET    /api/hr/persons/{id}/employment              → EmploymentPeriodView[]
//   POST   /api/hr/persons/{id}/employment              → EmploymentPeriodView
//
//   GET    /api/hr/job-titles?activeOnly                → JobTitleView[]
//   POST   /api/hr/job-titles                           → JobTitleView (201)
//   PUT    /api/hr/job-titles/{id}                      → JobTitleView
//
// Huquqlar: o'qish (uchalasi ham) `hr.person.read`, kadr yozuvi
// `hr.person.write`, TAYINLASH `hr.employment.write`, lavozim katalogi
// `hr.jobtitle.write`.
//
// =====================================================================
// ⚠️ 1-QOIDA — TARIX O'ZGARMAS (QARORLAR.md §20.9-E)
// =====================================================================
// Mijoz talabi so'zma-so'z: "muhim xodim ma'lumoti ustiga yozilmasin —
// sana, foydalanuvchi va sabab bilan tarix saqlansin".
//
// Shuning uchun bu faylda `updateEmployment` ham, `deleteEmployment` ham
// YO'Q va bo'lmaydi ham: backendda ular umuman mavjud emas
// (`EmploymentController` da faqat `GET` va `POST`), bazada esa
// `hr.employment_immutable()` triggeri `UPDATE` ni rad etadi.
//
// Panel tomonida buning oqibati aniq: tayinlash qatorida TAHRIRLASH va
// O'CHIRISH tugmasi CHIZILMAYDI. Yangi tayinlash — yangi qator.
//
// =====================================================================
// ⚠️ 2-QOIDA — TUGUN TURI NOMIGA EMAS, IMKONIYATIGA QARAB (§19.3)
// =====================================================================
// Xodim faqat `has_employees = true` turdagi tugunga biriktiriladi.
// Bu shart BAZADA (`hr.employment_guard`) va u turning IMKONIYATIGA
// qaraydi. Panel ham xuddi shunday qiladi — `employeeNodes()` ga
// qarang. Kodda `kind === 'BRANCH'` KABI SOLISHTIRISH YO'Q.
//
// ⚠️ Tugunlar ro'yxati BU SERVISDAN kelmaydi: `xsib-hr` da tugun
// endpointi yo'q va ataylab yo'q (§20.4 — "daraxt bitta egada").
// Ro'yxat `platform` dan olinadi (`features/org/orgApi`) va kesh
// kaliti ham o'shaniki (`queryKeys.org.*`) — ya'ni tashkiliy tuzilma
// sahifasi bilan BITTA kesh yozuvi bo'lishadi, ikkinchi so'rov ketmaydi.
//
// ⚠️ Buning narxi bor va u ochiq: tugunlar ro'yxati `org.node.read`
// huquqini talab qiladi. Faqat `hr.person.read` bor operator xodimlar
// RO'YXATINI to'liq ko'radi (tugun nomi javobning o'zida keladi), lekin
// tugun bo'yicha FILTRLAY olmaydi va yangi tayinlash bera olmaydi.
// Panel buni jimgina yashirmaydi — sababini yozib ko'rsatadi.
//
// =====================================================================
// ⚠️ 3-QOIDA — `hr.person` OBRO'LI MANBA, `attendance.employee` KESH
// =====================================================================
// §20.6: `attendance.employee.full_name` — faqat qurilma ekrani uchun
// NUSXA. Ikkalasi panelda ham ARALASHTIRILMAYDI: bu yerdagi sahifalar
// `attendance` API'siga umuman murojaat qilmaydi va teskarisi.
// Xodimning ismi FAQAT shu yerda tahrirlanadi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { type STATE_ALL, type STATE_OPEN_ONLY, showsClosed } from '@/shared/ui/table/closedFilter'
import type { OrgNode, OrgNodeType } from '@/features/org/orgApi'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * Xodim holati — `hr.person.state` dagi `CHECK` ning nusxasi.
 *
 * ⚠️ `TERMINATED` bu ro'yxatda BOR, lekin u `POST /state` orqali
 * QO'YILMAYDI: servis uni aniq xabar bilan rad etadi
 * (`PersonServiceImpl`), chunki ishdan bo'shatish sana talab qiladi va
 * ochiq tayinlash davrini ham yopadi. Shuning uchun quyida ikkinchi,
 * TORROQ ro'yxat bor.
 */
export const PERSON_STATES = ['ACTIVE', 'SUSPENDED', 'TERMINATED'] as const
export type PersonState = (typeof PERSON_STATES)[number]

/** `POST /api/hr/persons/{id}/state` qabul qiladigan yagona ikki qiymat */
export const PERSON_TOGGLE_STATES = ['ACTIVE', 'SUSPENDED'] as const

/**
 * Ish turi — backenddagi `EmploymentType` enum va bazadagi
 * `employment_type_valid` CHECK ning nusxasi.
 *
 * ⚠️ Bu qiymat kodda SOLISHTIRILMAYDI — na bu yerda, na backendda. U
 * faqat bazaga o'tadi va hisobot kesimi bo'lib qoladi. Yopiq ro'yxat
 * kerak, chunki erkin matn bo'lsa "FULL_TIME" / "Full time" /
 * "fulltime" uch guruhga bo'linib ketardi va hisobot JIMGINA noto'g'ri
 * bo'lardi (`MovementKind` javadoc'idagi bilan bir xil mulohaza).
 *
 * ⚠️ RO'YXAT UCH JOYDA va u BIRGA o'zgaradi: shu yer, backend
 * `EmploymentType`, migratsiyadagi CHECK. Ajralib ketmasligini
 * `tests/hr.mjs` (bu yer) va `EmploymentTypeTest` (backend ↔ baza)
 * qo'riqlaydi.
 *
 * ⚠️ Har qiymat uchun `employmentType.<QIYMAT>` tarjimasi UCHALA
 * tilda bo'lishi SHART — `tests/hr.mjs` buni ham tekshiradi. Endi
 * "noma'lum qiymatni xom ko'rsatish" yo'li YO'Q, ya'ni tarjima
 * yetishmasa ekranda kalitning O'ZI chizilardi.
 */
export const EMPLOYMENT_TYPES = [
  'FULL_TIME',
  'PART_TIME',
  'CONTRACT',
  'INTERN',
  'PROBATION',
] as const
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number]

/** `HrTextRules.PERSONNEL_NO` — FAQAT raqam, 32 belgigacha */
export const PERSONNEL_NO_PATTERN = /^[0-9]{1,32}$/
/** `HrTextRules.JOB_TITLE_CODE` */
export const JOB_TITLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,31}$/
/** `HrTextRules.NAME_MAX` */
export const NAME_MAX = 120
/** `HrTextRules.REASON_MAX` */
export const REASON_MAX = 500
/** `PersonDraft.ADDRESS_MAX` */
export const ADDRESS_MAX = 300

// --- Xodim -------------------------------------------------------------------

const personSchema = z.object({
  id: uuidSchema,
  personnelNo: z.string(),
  lastName: z.string(),
  firstName: z.string(),
  middleName: z.string().nullish(),
  /** ⚠️ BAZADAN keladi (`GENERATED ALWAYS`), panelda YIG'ILMAYDI */
  fullName: z.string(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  emergencyName: z.string().nullish(),
  emergencyPhone: z.string().nullish(),
  /** `LocalDate` — "YYYY-MM-DD", vaqt zonasi YO'Q */
  hiredAt: z.string(),
  terminatedAt: z.string().nullish(),
  state: z.enum(PERSON_STATES),
  appUserRef: uuidSchema.nullish(),
  photoFileId: uuidSchema.nullish(),
  contractFileId: uuidSchema.nullish(),
  /**
   * ⚠️ `nullish` — bu YERDA `null` bo'lishi MUMKIN, `employmentType`
   * bilan AYNAN bir sababdan: joriy tayinlash `LEFT JOIN LATERAL` bilan
   * olinadi va tayinlashsiz xodimda butun blok (`org_node_id`,
   * `job_title_id`, `employment_type`) `null` bo'ladi
   * (`PersonRepository#SCOPED_FROM`).
   *
   * ⚠️ Bu yerda `z.string()` turgani JIMGINA emas, BALAND yiqilardi va
   * oqibati bitta qatordan katta edi: `personPageSchema.parse` BUTUN
   * javobni rad etadi, ya'ni tayinlashsiz BITTA xodim butun xodimlar
   * RO'YXATINI xatoga aylantirardi. `PersonView` javadoc'i "null
   * bo'lishi mumkin emas" deydi, lekin u YARATISH yo'lini tasvirlaydi
   * (`PersonCreate` da tayinlash majburiy) — o'qish yo'li esa
   * `INNER JOIN` EMAS va bu ATAYLAB: aks holda bunday xodim ro'yxatdan
   * jimgina yo'qolardi.
   */
  orgNodeId: uuidSchema.nullish(),
  orgNodeCode: z.string().nullish(),
  orgNodeName: z.string().nullish(),
  orgNodePath: z.string().nullish(),
  jobTitleId: uuidSchema.nullish(),
  jobTitleCode: z.string().nullish(),
  jobTitleName: z.string().nullish(),
  /**
   * ⚠️ Bu YERDA `null` bo'lishi mumkin — joriy tayinlash
   * `LEFT JOIN LATERAL` bilan olinadi va tayinlashsiz xodimda butun
   * blok bo'sh bo'ladi (`PersonView.employmentType` javadoc'i).
   * Tayinlash tarixida esa (pastda) u har doim bor.
   */
  employmentType: z.enum(EMPLOYMENT_TYPES).nullish(),
  assignmentFrom: z.string().nullish(),
  /** ⚠️ `false` — joriy tugun ESKI, panel buni ko'rsatishi SHART */
  assignmentOpen: z.boolean(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface Person {
  id: string
  personnelNo: string
  lastName: string
  firstName: string
  middleName: string | null
  fullName: string
  phone: string | null
  email: string | null
  address: string | null
  emergencyName: string | null
  emergencyPhone: string | null
  hiredAt: string
  terminatedAt: string | null
  state: PersonState
  appUserRef: string | null
  photoFileId: string | null
  contractFileId: string | null
  /** `null` — joriy tayinlash yo'q (`personSchema.orgNodeId` izohi) */
  orgNodeId: string | null
  orgNodeCode: string | null
  orgNodeName: string | null
  orgNodePath: string | null
  jobTitleId: string | null
  jobTitleCode: string | null
  jobTitleName: string | null
  employmentType: EmploymentType | null
  assignmentFrom: string | null
  assignmentOpen: boolean
  createdAt: string | null
  updatedAt: string | null
}

function toPerson(raw: z.infer<typeof personSchema>): Person {
  return {
    id: raw.id,
    personnelNo: raw.personnelNo,
    lastName: raw.lastName,
    firstName: raw.firstName,
    middleName: raw.middleName ?? null,
    fullName: raw.fullName,
    phone: raw.phone ?? null,
    email: raw.email ?? null,
    address: raw.address ?? null,
    emergencyName: raw.emergencyName ?? null,
    emergencyPhone: raw.emergencyPhone ?? null,
    hiredAt: raw.hiredAt,
    terminatedAt: raw.terminatedAt ?? null,
    state: raw.state,
    appUserRef: raw.appUserRef ?? null,
    photoFileId: raw.photoFileId ?? null,
    contractFileId: raw.contractFileId ?? null,
    orgNodeId: raw.orgNodeId ?? null,
    orgNodeCode: raw.orgNodeCode ?? null,
    orgNodeName: raw.orgNodeName ?? null,
    orgNodePath: raw.orgNodePath ?? null,
    jobTitleId: raw.jobTitleId ?? null,
    jobTitleCode: raw.jobTitleCode ?? null,
    jobTitleName: raw.jobTitleName ?? null,
    employmentType: raw.employmentType ?? null,
    assignmentFrom: raw.assignmentFrom ?? null,
    assignmentOpen: raw.assignmentOpen,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

const personPageSchema = z.object({
  rows: z.array(personSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

/**
 * Bitta xodim javobini tekshiradi va panel modeliga o'giradi.
 *
 * ⚠️ ALOHIDA VA EKSPORT QILINGAN — `tests/hr.mjs` uni HAQIQIY javob
 * shakli ustida yurgizadi. Sxema HTTP chaqiruvi ichida yashiringan
 * bo'lsa sinov unga umuman yeta olmasdi, holbuki aynan shu yer bir marta
 * jimgina yiqilgan edi: tayinlashsiz xodimda `orgNodeId` `null` keladi
 * va qat'iy sxema BUTUN ro'yxatni rad etardi.
 */
export function parsePerson(raw: unknown): Person {
  return toPerson(personSchema.parse(raw))
}

/** Ro'yxat javobi — {@link parsePerson} bilan bir xil sabab bo'yicha eksport qilingan */
export function parsePersonPage(raw: unknown): PageResult<Person> {
  const parsed = personPageSchema.parse(raw)
  return { items: parsed.rows.map(toPerson), total: parsed.total }
}

/**
 * Xodim uchun «yopiq» — FAQAT `TERMINATED` (`PersonState.isClosed()`).
 *
 * ⚠️ `SUSPENDED` ro'yxatda QOLADI: u VAQTINCHA to'xtatilgan va qaytadi.
 * Uni yashirish «bu odam ishdan ketgan» degan yolg'onni ko'rsatardi va
 * qayta yoqish amali ham o'sha qator bilan birga yo'qolardi.
 */
export const PERSON_CLOSED_STATE: PersonState = 'TERMINATED'

/**
 * Holat tanlovi: aniq holat YOKI ikki maxsus qiymatdan biri
 * ({@code STATE_OPEN_ONLY} — sukut, {@code STATE_ALL} — hammasi).
 */
export type PersonStateFilter =
  | PersonState
  | typeof STATE_OPEN_ONLY
  | typeof STATE_ALL

function isPersonState(value: string): value is PersonState {
  return (PERSON_STATES as readonly string[]).includes(value)
}

export interface PersonFilter {
  state: PersonStateFilter
  /**
   * ⚠️ AYNAN shu tugundagilar — `SUBTREE` EMAS (`PersonController`
   * javadoc'i). "Filial va uning ostidagilar" — bu ko'rinish DOIRASI va
   * u tokendan keladi; bu esa oddiy filtr. Panelda shu farq matn bilan
   * aytiladi, aks holda operator "filialni tanladim, bo'limdagilar
   * qayoqqa ketdi?" degan savolga tushardi.
   */
  orgNodeId: string | null
}

/**
 * So'rov parametrlari — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `includeClosed` DOIM yuboriladi: serverdagi sukut qiymat `true`
 * (`PersonController#list`), ya'ni parametrsiz so'rov ishdan
 * bo'shaganlarni ham qaytaradi. Yashirish qarori PANELNIKI.
 *
 * ⚠️ `state` aniq berilganda server bayroqni E'TIBORSIZ qoldiradi
 * (`PersonServiceImpl`: `excludeClosed = !includeClosed && state == null`)
 * — usiz «faqat ishdan bo'shaganlarni ko'rsat» DOIM bo'sh natija
 * berardi.
 */
export function personListParams(
  filter: PersonFilter,
  table: TableState,
): Record<string, unknown> {
  const search = table.search.trim()
  return {
    ...(search === '' ? {} : { q: search }),
    ...(isPersonState(filter.state) ? { state: filter.state } : {}),
    ...(filter.orgNodeId ? { orgNodeId: filter.orgNodeId } : {}),
    includeClosed: showsClosed(filter.state),
    // Backend sahifani 0 dan sanaydi — `/api/inventory/equipment` bilan bir xil
    page: table.page - 1,
    size: table.pageSize,
  }
}

export function fetchPersonPage(
  filter: PersonFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Person>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/hr/persons', {
      signal,
      params: personListParams(filter, state),
    })
    return parsePersonPage(response.data)
  }
}

/**
 * Sukut ko'rinish NECHTA xodimni yashirganini sanaydi.
 *
 * ⚠️ Holat AYNAN `TERMINATED` ga qo'yiladi — server shu holatda
 * bayroqni e'tiborsiz qoldiradi va qatorlarni ROSTDAN qaytaradi.
 * `size: 1` — javobdan faqat `total` kerak.
 */
export function personClosedCountParams(
  filter: PersonFilter,
  table: TableState,
): Record<string, unknown> {
  return {
    ...personListParams({ ...filter, state: PERSON_CLOSED_STATE }, table),
    page: 0,
    size: 1,
  }
}

export function countClosedPersons(
  filter: PersonFilter,
): (state: TableState, signal: AbortSignal) => Promise<number> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/hr/persons', {
      signal,
      params: personClosedCountParams(filter, state),
    })
    return parsePersonPage(response.data).total
  }
}

/*
 * ⚠️ BU YERDA `fetchPersonIndex` BOR EDI — `id → ism` ko'rsatkichi,
 * maosh ro'yxati uchun. U 2026-08-14 da OLIB TASHLANDI: backend
 * `PayItemView.fullName` ni qaytaradigan bo'ldi va ism endi javobning
 * O'ZIDA keladi (`payApi.ts` 4-qoidasi). Ko'rsatkich bilan birga
 * `PERSON_INDEX_LIMIT` (200) va uning ekrandagi ogohlantirishi ham
 * ketdi.
 *
 * ⚠️ Qaytarmang: ro'yxat `ORDER BY p.full_name` bilan saralanadi,
 * ya'ni ikkinchi manba tartib va ismni bir-biridan ajratardi.
 */

export async function fetchPerson(id: string): Promise<Person> {
  const response = await api.get<unknown>(`/api/hr/persons/${id}`)
  return parsePerson(response.data)
}

/**
 * Kadr yozuvi — `PersonDraft`.
 *
 * ⚠️ LAVOZIM VA TUGUN BU YERDA YO'Q va bu ataylab (`PersonDraft`
 * javadoc'i): ular tarixda yashaydi. Nusxasini bu yerga qo'yish
 * "kartochkada Mazar, tarixda Kabul" holatini yaratardi.
 *
 * ⚠️ `fullName` ham yo'q — u bazada hosila ustun.
 */
export interface PersonDraft {
  personnelNo: string
  lastName: string
  firstName: string
  middleName: string | null
  phone: string | null
  email: string | null
  address: string | null
  /** ⚠️ `emergencyPhone` bilan IKKISI BIRGA yoki ikkisi ham `null` */
  emergencyName: string | null
  emergencyPhone: string | null
  /** "YYYY-MM-DD" */
  hiredAt: string
  /** `identity.app_user.id` yoki `null` — `null` ODATIY HOL */
  appUserRef: string | null
}

/**
 * Tayinlash — yangi davr.
 *
 * ⚠️ `validFrom` `null` bo'lsa servis bugungi kunni qo'yadi (yaratishda
 * — `hiredAt`). Panel uni MAJBURIY qilmaydi, chunki eng ko'p uchraydigan
 * holat aynan "bugundan".
 */
export interface EmploymentAssignment {
  orgNodeId: string
  jobTitleId: string
  employmentType: EmploymentType
  validFrom: string | null
  /** ⚠️ MAJBURIY — sababsiz tarix audit jurnalidan farq qilmaydi */
  reason: string
}

export async function createPerson(
  person: PersonDraft,
  assignment: EmploymentAssignment,
): Promise<Person> {
  const response = await api.post<unknown>('/api/hr/persons', { person, assignment })
  return parsePerson(response.data)
}

export async function updatePerson(id: string, draft: PersonDraft): Promise<Person> {
  const response = await api.put<unknown>(`/api/hr/persons/${id}`, draft)
  return parsePerson(response.data)
}

/**
 * Ishdan bo'shatish — `DELETE` O'RNIDA.
 *
 * ⚠️ Xodim O'CHIRILMAYDI: uning tayinlash tarixi maosh hisobotining
 * manbai (§20.9-E) va o'chirish o'tgan yillarni hisoblab bo'lmaydigan
 * qilardi.
 */
export async function terminatePerson(
  id: string,
  terminatedAt: string,
  reason: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/persons/${id}/termination`, {
    terminatedAt,
    reason,
  })
  return operationResultSchema.parse(response.data)
}

export const PERSON_ATTACHMENT_KINDS = ['PHOTO', 'CONTRACT'] as const
export type PersonAttachmentKind = (typeof PERSON_ATTACHMENT_KINDS)[number]

/** xsib-file chegarasi (ADR-037) */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024

export const ATTACHMENT_ACCEPT: Record<PersonAttachmentKind, readonly string[]> = {
  PHOTO: ['image/jpeg', 'image/png', 'image/webp'],
  CONTRACT: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
}

export function attachmentOf(person: Person, kind: PersonAttachmentKind): string | null {
  return kind === 'PHOTO' ? person.photoFileId : person.contractFileId
}

export async function uploadPersonAttachment(
  personId: string,
  kind: PersonAttachmentKind,
  file: File,
): Promise<Person> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.put<unknown>(`/api/hr/persons/${personId}/attachments/${kind}`, form, {
    headers: { 'Content-Type': undefined },
  })
  return parsePerson(response.data)
}

export async function removePersonAttachment(
  personId: string,
  kind: PersonAttachmentKind,
): Promise<Person> {
  const response = await api.delete<unknown>(`/api/hr/persons/${personId}/attachments/${kind}`)
  return parsePerson(response.data)
}

export async function fetchPersonAttachmentUrl(
  personId: string,
  kind: PersonAttachmentKind,
): Promise<string> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/attachments/${kind}/url`)
  return z.object({ url: z.string() }).parse(response.data).url
}

/** `ACTIVE` ↔ `SUSPENDED`. ⚠️ `TERMINATED` — yuqoridagi yo'l. */
export async function setPersonState(
  id: string,
  state: (typeof PERSON_TOGGLE_STATES)[number],
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/persons/${id}/state`, { state })
  return operationResultSchema.parse(response.data)
}

// --- Tayinlash tarixi --------------------------------------------------------

const employmentPeriodSchema = z.object({
  id: uuidSchema,
  orgNodeId: uuidSchema,
  orgNodeCode: z.string().nullish(),
  orgNodeName: z.string().nullish(),
  orgNodePath: z.string().nullish(),
  jobTitleId: uuidSchema.nullish(),
  jobTitleCode: z.string().nullish(),
  jobTitleName: z.string().nullish(),
  /**
   * ⚠️ `nullish` EMAS: ustun `NOT NULL` va qiymatlar to'plami
   * `employment_type_valid` bilan yopilgan. Ixtiyoriy qilib qo'yish
   * panelga "bo'lmasligi ham mumkin" degan o'lik shox qo'shardi va
   * o'lik shox sinovdan o'tmagani uchun bir kun jimgina noto'g'ri
   * ishlab ketardi.
   */
  employmentType: z.enum(EMPLOYMENT_TYPES),
  validFrom: z.string(),
  /** `null` = JORIY davr */
  validTo: z.string().nullish(),
  reason: z.string().nullish(),
  changedBy: z.string().nullish(),
  createdAt: instantSchema.nullish(),
})

export interface EmploymentPeriod {
  id: string
  orgNodeId: string
  orgNodeCode: string | null
  orgNodeName: string | null
  orgNodePath: string | null
  jobTitleId: string | null
  jobTitleCode: string | null
  jobTitleName: string | null
  employmentType: EmploymentType
  validFrom: string
  validTo: string | null
  reason: string | null
  changedBy: string | null
  createdAt: string | null
}

function toEmploymentPeriod(raw: z.infer<typeof employmentPeriodSchema>): EmploymentPeriod {
  return {
    id: raw.id,
    orgNodeId: raw.orgNodeId,
    orgNodeCode: raw.orgNodeCode ?? null,
    orgNodeName: raw.orgNodeName ?? null,
    orgNodePath: raw.orgNodePath ?? null,
    jobTitleId: raw.jobTitleId ?? null,
    jobTitleCode: raw.jobTitleCode ?? null,
    jobTitleName: raw.jobTitleName ?? null,
    employmentType: raw.employmentType,
    validFrom: raw.validFrom,
    validTo: raw.validTo ?? null,
    reason: raw.reason ?? null,
    changedBy: raw.changedBy ?? null,
    createdAt: raw.createdAt ?? null,
  }
}

/** Butun tarix, eng yangisi tepada (server tartibi saqlanadi) */
export async function fetchEmploymentHistory(personId: string): Promise<readonly EmploymentPeriod[]> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/employment`)
  return z.array(employmentPeriodSchema).parse(response.data).map(toEmploymentPeriod)
}

export async function assignEmployment(
  personId: string,
  assignment: EmploymentAssignment,
): Promise<EmploymentPeriod> {
  const response = await api.post<unknown>(`/api/hr/persons/${personId}/employment`, assignment)
  return toEmploymentPeriod(employmentPeriodSchema.parse(response.data))
}

// --- Lavozim katalogi --------------------------------------------------------

const jobTitleSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  /** Shu lavozimda HOZIR ishlayotganlar soni (ochiq davr bo'yicha) */
  personCount: z.number(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface JobTitle {
  id: string
  code: string
  name: string
  active: boolean
  personCount: number
  createdAt: string | null
  updatedAt: string | null
}

function toJobTitle(raw: z.infer<typeof jobTitleSchema>): JobTitle {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    active: raw.active,
    personCount: raw.personCount,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

/**
 * @param activeOnly tayinlash oynasida `true` (o'chirilgan lavozim
 *                   tanlanmasin), katalog sahifasida `false`
 */
export async function fetchJobTitles(activeOnly: boolean): Promise<readonly JobTitle[]> {
  const response = await api.get<unknown>('/api/hr/job-titles', { params: { activeOnly } })
  return z.array(jobTitleSchema).parse(response.data).map(toJobTitle)
}

export interface JobTitleDraft {
  /** ⚠️ `^[A-Z][A-Z0-9_]{1,31}$`. Yaratilgandan keyin ham o'zgartirilishi mumkin, lekin u ishoradir — tavsiya etilmaydi */
  code: string
  name: string
  /**
   * ⚠️ `false` — yangi tayinlashda tanlanmaydi, MAVJUD tarixda qoladi.
   * O'chirish yo'li YO'Q: `employment_job_title_fk` `RESTRICT` va bu
   * ataylab — lavozimni o'chirish o'tgan davrlarni nomsiz qoldirardi.
   */
  active: boolean
}

export async function createJobTitle(draft: JobTitleDraft): Promise<JobTitle> {
  const response = await api.post<unknown>('/api/hr/job-titles', draft)
  return toJobTitle(jobTitleSchema.parse(response.data))
}

export async function updateJobTitle(id: string, draft: JobTitleDraft): Promise<JobTitle> {
  const response = await api.put<unknown>(`/api/hr/job-titles/${id}`, draft)
  return toJobTitle(jobTitleSchema.parse(response.data))
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Xodim biriktirilishi MUMKIN bo'lgan tugunlar — §19.3.
 *
 * ⚠️ Qaror turning NOMIGA emas, `hasEmployees` IMKONIYATIGA qarab
 * chiqariladi. Administrator "Uchastka" degan yangi tur ochib
 * `has_employees = true` qo'ysa, ro'yxat darhol uni ham ko'rsatadi va
 * bitta qator kod o'zgarmaydi.
 *
 * ⚠️ Turi ro'yxatda TOPILMAGAN tugun CHIQARIB TASHLANADI, "shubha
 * foydasiga" qoldirilmaydi. Sabab: bu ro'yxat yozish uchun ishlatiladi
 * va noto'g'ri tugun tanlansa baza uni rad etadi (`hr.employment_guard`)
 * — operator sababi tushunarsiz 400 olardi. `canPlaceUnder` dagi
 * teskari qaror ("tur noma'lum — taqiq yo'q") boshqa holat uchun: u
 * yerda gap TAKLIFNI cheklash haqida, bu yerda esa YAROQSIZ tanlovni
 * ko'rsatmaslik haqida.
 *
 * ⚠️ `CLOSED` tugun ham chiqariladi: yopilgan bo'limga odam
 * tayinlanmaydi. `SUSPENDED` esa QOLADI — u vaqtinchalik holat.
 */
export function employeeNodes(
  nodes: readonly OrgNode[],
  types: readonly OrgNodeType[],
): readonly OrgNode[] {
  const withEmployees = new Set(
    types.filter((type) => type.hasEmployees).map((type) => type.code),
  )
  return nodes.filter((node) => node.state !== 'CLOSED' && withEmployees.has(node.kind))
}

/**
 * Davr JORIYmi.
 *
 * ⚠️ Yagona mezon — `validTo === null`. "Eng yangi qator joriy" degan
 * taxmin NOTO'G'RI bo'lardi: xodim ishdan bo'shaganda oxirgi davr ham
 * yopiladi va u holda ochiq davr UMUMAN qolmaydi.
 */
export function isCurrentPeriod(period: EmploymentPeriod): boolean {
  return period.validTo === null
}

/**
 * Ismning qisqa shakli — jadval va tanlagichlar uchun.
 *
 * ⚠️ `fullName` ni QAYTA yig'MAYDI, uni QISQARTIRADI: to'liq ism
 * baribir serverdan keladi (§20.6 — obro'li manba), bu yerda faqat
 * tabel raqami bilan ajratib ko'rsatiladi.
 */
export function personLabel(person: Person): string {
  return `${person.fullName} · ${person.personnelNo}`
}
